import { UserCompany } from "src/app/pages/users/user.model";

export function getVisibleUserCompanies(
  userCompanies: UserCompany[] | null | undefined
): UserCompany[] {
  return (userCompanies ?? []).filter((uc) => uc.isHidden !== true);
}

export function getUserCompanyId(userCompany: UserCompany): string | null {
  return userCompany.companyId || userCompany.company?.id || null;
}

export function findPreferredUserCompany(
  userCompanies: UserCompany[] | null | undefined,
  preferredCompanyId: string | null | undefined
): UserCompany | null {
  const id = preferredCompanyId?.trim().toLowerCase();
  if (!id) {
    return null;
  }
  return (
    getVisibleUserCompanies(userCompanies).find(
      (uc) => getUserCompanyId(uc)?.toLowerCase() === id
    ) ?? null
  );
}

export type AdminOrgDecision = "allowed" | "select-company";

export function hasMultipleVisibleCompanies(
  userCompanies: UserCompany[] | null | undefined
): boolean {
  return getVisibleUserCompanies(userCompanies).length > 1;
}

/** Multi-org users must pick a company before entering admin (unless launch handoff set org). */
export function shouldRequireCompanySelection(input: {
  userCompanies?: UserCompany[] | null;
  requestedCompanyId?: string | null;
}): boolean {
  if (input.requestedCompanyId?.trim()) {
    return false;
  }
  return hasMultipleVisibleCompanies(input.userCompanies);
}

/** Whether the session has an active organization or the user must pick one. */
export function resolveAdminOrganizationContext(input: {
  companyId?: string | null;
  userCompanies?: UserCompany[] | null;
  /** Super Global Admin browsing All organizations (no single company required). */
  allOrganizationsScope?: boolean;
  /** @deprecated Prefer superGlobalAdmin for All-orgs scope. */
  globalAdmin?: boolean;
  superGlobalAdmin?: boolean;
}): AdminOrgDecision {
  if (input.superGlobalAdmin && input.allOrganizationsScope) {
    return "allowed";
  }
  if (input.companyId?.trim()) {
    return "allowed";
  }
  if (getVisibleUserCompanies(input.userCompanies).length === 0) {
    return "allowed";
  }
  return "select-company";
}

/** Numeric menu ids for Super Global Admin + All organizations queues (MenuItemsEnum). */
export const SUPER_ADMIN_QUEUE_MENU_IDS = new Set<number>([
  7, // ProfessionalProfiles
  8, // PracticeRequests
  9, // ProviderProfiles
]);

export function isSuperGlobalAdmin(user: {
  superGlobalAdmin?: boolean;
} | null | undefined): boolean {
  return !!user?.superGlobalAdmin;
}
